import type {
  ActivityDTO,
  AgentDTO,
  AssignmentDTO,
  DashboardMetricsDTO,
  DemoDTO,
  EmulatorSessionDTO,
  ImportJobDTO,
  MergePreviewDTO,
  PortalDTO,
  PresentationDTO,
  ProspectDTO,
  SettingsDTO,
} from "@mm-sales/shared";
import { getToken, launchWindowName } from "./auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  // A request must never hang the UI: the dev proxy can leave a call pending
  // while the API restarts, which showed as "Signing you in…" forever.
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      signal: AbortSignal.timeout(method === "GET" ? 20_000 : 120_000),
    });
  } catch (err) {
    const timedOut = err instanceof Error && err.name === "TimeoutError";
    throw new ApiError(
      0,
      timedOut ? "timeout" : "network",
      timedOut ? "The studio API didn't answer in time." : "Could not reach the studio API.",
      null,
    );
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // An HTML error page from the dev proxy (API down) is not JSON.
    throw new ApiError(
      res.status || 0,
      "bad_response",
      `Unexpected reply from the API (${res.status})`,
      null,
    );
  }
  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; message?: string; details?: unknown };
    const code = d.error ?? "error";
    const message =
      d.message ??
      (typeof d.details === "object" ? "Please check the form" : `Request failed (${res.status})`);
    throw new ApiError(res.status, code, message, data);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, b?: unknown) => request<T>("POST", p, b),
  /** Multipart upload (a FormData body); the browser sets the boundary header. */
  upload: <T>(p: string, fd: FormData) => request<T>("POST", p, fd),
  put: <T>(p: string, b?: unknown) => request<T>("PUT", p, b),
  patch: <T>(p: string, b?: unknown) => request<T>("PATCH", p, b),
  del: <T>(p: string) => request<T>("DELETE", p),
};

export type Me = {
  agent: AgentDTO;
  settings: SettingsDTO;
  capabilities: {
    provisioner: string;
    broker: string;
    /** Which model writes presentations; null → rules-based deck from the site audit. */
    ai: "anthropic" | "openai" | null;
    /** Image generation (menu photos, slide images) is configured. */
    images: boolean;
    importers: Record<string, boolean>;
  };
};
export type Page<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};

/**
 * The prospect's portal opened by the rep: same page the prospect sees, plus the live
 * emulators, because the rep's token rides along (stripped from the URL on load). A new tab
 * gets no sessionStorage of its own, hence the query parameter.
 */
export const launchUrl = (shareToken: string) => {
  const t = getToken();
  return `/p/${shareToken}${t ? `?token=${encodeURIComponent(t)}` : ""}`;
};

/**
 * "Launch": open one demo's portal in its own full-screen window. The studio itself
 * is embedded in MyManager, so the portal must leave the frame; a popup sized to the
 * whole screen gives the rep a clean, chrome-less window per demo (one per share
 * token, so relaunching focuses the existing window). The token travels in the URL,
 * so the new window needs no handshake with anyone.
 */
export const openDemoWindow = (shareToken: string) => {
  const url = new URL(launchUrl(shareToken), window.location.origin).toString();
  const w = window.screen?.availWidth || window.outerWidth || 1440;
  const h = window.screen?.availHeight || window.outerHeight || 900;
  const name = launchWindowName(shareToken);
  const win = window.open(
    url,
    name,
    `popup=yes,width=${w},height=${h},left=0,top=0,resizable=yes,scrollbars=yes`,
  );
  if (win) win.focus();
  // Popup blocked: a normal tab. Same window name and no "noopener", so the portal can
  // still tell it was launched from here and accept the token in the URL.
  else window.open(url, name);
};
/** The account that holds a prospect, in MyManager terms. */
export type OwnerRef = {
  id: string;
  name: string;
  kind: string;
  role?: string;
  orgName?: string;
  locationName?: string;
};
export type ProspectListItem = ProspectDTO & { owner?: OwnerRef | null };
export type ProspectDetail = ProspectDTO & {
  owner: OwnerRef | null;
  sourcedBy: { id: string; name: string } | null;
};

export const q = {
  me: () => api.get<Me>("/v1/me"),
  metrics: (params: Record<string, string | undefined> = {}) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
    const qs = s.toString();
    return api.get<DashboardMetricsDTO>(`/v1/metrics/dashboard${qs ? `?${qs}` : ""}`);
  },
  prospects: (params: Record<string, string | number | undefined> = {}) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(params))
      if (v !== undefined && v !== "") s.set(k, String(v));
    return api.get<Page<ProspectListItem>>(`/v1/prospects?${s}`);
  },
  prospect: (id: string) => api.get<ProspectDetail>(`/v1/prospects/${id}`),
  prospectDemos: (id: string) => api.get<{ items: DemoDTO[] }>(`/v1/prospects/${id}/demos`),
  prospectActivity: (id: string) =>
    api.get<{ items: ActivityDTO[] }>(`/v1/prospects/${id}/activity`),
  prospectAssignments: (id: string) =>
    api.get<{
      items: AssignmentDTO[];
      agents: Record<string, { name: string; kind: string }>;
    }>(`/v1/prospects/${id}/assignments`),
  prospectImports: (id: string) =>
    api.get<{ items: ImportJobDTO[] }>(`/v1/prospects/${id}/imports`),
  presentation: (id: string) => api.get<PresentationDTO>(`/v1/prospects/${id}/presentation`),
  importJob: (id: string) => api.get<ImportJobDTO>(`/v1/imports/${id}`),
  /** What several finished imports would merge into (nothing written). */
  mergePreview: (prospectId: string, jobIds: string[]) =>
    api.post<MergePreviewDTO>("/v1/imports/merge", { prospectId, jobIds }),
  detect: (url: string) =>
    api.get<{
      platform: string | null;
      adapter: { label: string; reliability: string } | null;
    }>(`/v1/imports/detect?url=${encodeURIComponent(url)}`),
  agents: (params: Record<string, string | undefined> = {}) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
    return api.get<{ items: AgentDTO[] }>(`/v1/agents?${s}`);
  },
  settings: () => api.get<SettingsDTO>("/v1/settings"),
  activeSessions: () => api.get<{ items: EmulatorSessionDTO[] }>("/v1/sessions/active"),
  capacity: () => api.get<{ total: number; free: number }>("/v1/sessions/capacity"),
  portal: (token: string) => api.get<PortalDTO>(`/v1/portal/${token}`),
  demo: (id: string) => api.get<DemoDTO>(`/v1/demos/${id}`),
};
